/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 3 PRO React context
import { useMaterialUIController, setLayout } from "context";

// Mock Authentication Service (client-side only)
import {
  verifyCredentials,
  generateMockTokens,
  getUserInfo,
  generate2FASetupURI,
  get2FASecret,
  verify2FACode,
} from "services/mockAuth";

// Context
import { useAuthContext } from "contextAuth";
import { useCookiesContext } from "contextCookies";
import { FRESENIUS } from "contextBranding";

function SignIn() {
  const { t } = useTranslation();
  // Login step state: 'credentials', '2fa-setup', '2fa-verify'
  const [loginStep, setLoginStep] = useState("credentials");
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [qrCodeURI, setQrCodeURI] = useState("");
  const [toastSuccess, setToastSuccess] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [toastTitle, setToastTitle] = useState("");
  const [toastContent, setToastContent] = useState("");

  const { loginUser, isInitialized, checkRefreshUser } = useAuthContext();
  const { getCookie, setCookie } = useCookiesContext();
  const [, dispatch] = useMaterialUIController();

  // Hide sidebar on mount
  useEffect(() => {
    setLayout(dispatch, "page");
  }, [dispatch]);

  // Toast Objects + Logic
  const openToastSuccess = (titel, content) => {
    setToastTitle(titel);
    setToastContent(content);
    setToastSuccess(true);
  };
  const closeToastSuccess = () => setToastSuccess(false);
  const openToastError = (titel, content) => {
    setToastTitle(titel);
    setToastContent(content);
    setToastError(true);
  };
  const closeToastError = () => setToastError(false);

  const renderSuccess = (
    <MDSnackbar
      color="succcess"
      icon="check"
      title={toastTitle}
      content={toastContent}
      dateTime=""
      open={toastSuccess}
      onClose={closeToastSuccess}
      close={closeToastSuccess}
      bgWhite
    />
  );

  const renderError = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={toastTitle}
      content={toastContent}
      dateTime=""
      open={toastError}
      onClose={closeToastError}
      close={closeToastError}
      bgWhite
    />
  );

  const onPageLoad = async () => {
    if (isInitialized) {
      const logInDataFound = await checkRefreshUser();

      if (logInDataFound) {
        window.location.href = window.location.origin + "/#/landing";
      }
    }
  };
  useEffect(() => {
    onPageLoad();
  }, []);

  // Step 1: Handle credentials submission
  const handleCredentialsSubmit = () => {
    if (credentials.email === "" || credentials.password === "") {
      openToastError(t("signIn.toastEmailOrPasswordMissing"), t("signIn.toastTryAgain"));
      return;
    }

    // Verify credentials client-side
    const isValid = verifyCredentials(credentials.email, credentials.password);

    if (isValid) {
      // Check if 2FA is already set up
      const setup2FA = getCookie("2fa_setup_complete") === "true";

      if (setup2FA) {
        // User has already set up 2FA, show verification screen
        setLoginStep("2fa-verify");
      } else {
        // First time login, show 2FA setup screen
        const uri = generate2FASetupURI();
        setQrCodeURI(uri);
        setLoginStep("2fa-setup");
      }
    } else {
      openToastError(t("signIn.toastEmailOrPasswordWrong"), t("signIn.toastTryAgain"));
    }
  };

  // Step 2a: Handle 2FA setup (first time)
  const handle2FASetup = () => {
    if (twoFactorCode === "" || twoFactorCode.length !== 6) {
      openToastError(t("signIn.toastInvalidCode"), t("signIn.toastCodeLength"));
      return;
    }

    const isValid = verify2FACode(twoFactorCode);

    if (isValid) {
      // Save 2FA setup status in cookie (1 year expiry)
      setCookie("2fa_setup_complete", "true", 365 * 24 * 60 * 60 * 1000);
      completeLogin();
    } else {
      openToastError(t("signIn.toastInvalidCode"), t("signIn.toastTryAgain"));
      setTwoFactorCode("");
    }
  };

  // Step 2b: Handle 2FA verification (subsequent logins)
  const handle2FAVerify = () => {
    if (twoFactorCode === "" || twoFactorCode.length !== 6) {
      openToastError(t("signIn.toastInvalidCode"), t("signIn.toastCodeLength"));
      return;
    }

    const isValid = verify2FACode(twoFactorCode);

    if (isValid) {
      completeLogin();
    } else {
      openToastError(t("signIn.toastInvalidCode"), t("signIn.toastTryAgain"));
      setTwoFactorCode("");
    }
  };

  // Complete login with mock tokens
  const completeLogin = () => {
    const tokens = generateMockTokens();
    const userInfo = getUserInfo();
    loginUser(tokens, userInfo);
    openToastSuccess(t("signIn.toastSuccess"), "");
    setTimeout(() => {
      window.location.href = window.location.origin + "/#/landing";
    }, 1000);
  };

  // Handle Enter key press
  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") {
      action();
    }
  };
  // Fresenius branding for login/2FA (default)
  const cardBg = `${FRESENIUS.blue}ee`;
  const cardBorder = `2px solid ${FRESENIUS.borderStrong}`;
  const inputBg = `${FRESENIUS.blueDark}99`;
  const textGradient = `linear-gradient(135deg, ${FRESENIUS.white} 0%, ${FRESENIUS.blueLight} 100%)`;

  // Render credentials view
  const renderCredentialsView = () => (
    <Card
      sx={{
        backgroundColor: cardBg,
        backdropFilter: "blur(10px)",
        border: cardBorder,
        borderRadius: "20px",
      }}
    >
      <MDBox pt={4} pb={3} px={3}>
        {/* Heading Section */}
        <MDBox textAlign="center" mb={4}>
          <MDTypography
            variant="h2"
            fontWeight="bold"
            color="white"
            mb={2}
            sx={{
              background: textGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("signIn.welcome")}
          </MDTypography>
          <MDTypography variant="body1" color="white" sx={{ opacity: 0.7 }}>
            {t("signIn.pleaseSignIn")}
          </MDTypography>
        </MDBox>

        <MDBox component="form" role="form">
          <MDBox mb={3}>
            <MDInput
              type="text"
              label={t("signIn.username")}
              value={credentials.email}
              onChange={(e) =>
                setCredentials((prev) => ({ ...prev, email: e.target.value }))
              }
              onKeyPress={(e) => handleKeyPress(e, handleCredentialsSubmit)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: `${inputBg} !important`,
                  color: `${FRESENIUS.white} !important`,
                },
                "& .MuiInputBase-input": {
                  color: `${FRESENIUS.white} !important`,
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: `0 0 0 1000px ${inputBg} inset !important`,
                    WebkitTextFillColor: `${FRESENIUS.white} !important`,
                    caretColor: `${FRESENIUS.white} !important`,
                    transition: "background-color 5000s ease-in-out 0s",
                    fontSize: "inherit !important",
                    fontFamily: "inherit !important",
                    fontWeight: "inherit !important",
                  },
                  "&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
                    WebkitBoxShadow: `0 0 0 1000px ${inputBg} inset !important`,
                    WebkitTextFillColor: `${FRESENIUS.white} !important`,
                  },
                  "&:-webkit-autofill::first-line": {
                    fontSize: "inherit !important",
                    fontFamily: "inherit !important",
                    color: `${FRESENIUS.white} !important`,
                  },
                },
                "& .MuiInputLabel-root": { color: `${FRESENIUS.white}B3 !important` },
                "& .MuiInputLabel-root.Mui-focused": { color: `${FRESENIUS.white} !important` },
                "& .MuiOutlinedInput-root fieldset": { borderColor: `${FRESENIUS.white}4D !important` },
                "& .MuiOutlinedInput-root:hover fieldset": { borderColor: `${FRESENIUS.white}80 !important` },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${FRESENIUS.white} !important` },
              }}
              inputProps={{
                autoComplete: "username",
              }}
            />
          </MDBox>
          <MDBox mb={4}>
            <MDInput
              type="password"
              label={t("signIn.password")}
              value={credentials.password}
              onChange={(e) =>
                setCredentials((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              onKeyPress={(e) => handleKeyPress(e, handleCredentialsSubmit)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: `${inputBg} !important`,
                  color: `${FRESENIUS.white} !important`,
                },
                "& .MuiInputBase-input": {
                  color: `${FRESENIUS.white} !important`,
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: `0 0 0 1000px ${inputBg} inset !important`,
                    WebkitTextFillColor: `${FRESENIUS.white} !important`,
                    caretColor: `${FRESENIUS.white} !important`,
                    transition: "background-color 5000s ease-in-out 0s",
                    fontSize: "inherit !important",
                    fontFamily: "inherit !important",
                    fontWeight: "inherit !important",
                  },
                  "&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
                    WebkitBoxShadow: `0 0 0 1000px ${inputBg} inset !important`,
                    WebkitTextFillColor: `${FRESENIUS.white} !important`,
                  },
                  "&:-webkit-autofill::first-line": {
                    fontSize: "inherit !important",
                    fontFamily: "inherit !important",
                    color: `${FRESENIUS.white} !important`,
                  },
                },
                "& .MuiInputLabel-root": { color: `${FRESENIUS.white}B3 !important` },
                "& .MuiInputLabel-root.Mui-focused": { color: `${FRESENIUS.white} !important` },
                "& .MuiOutlinedInput-root fieldset": { borderColor: `${FRESENIUS.white}4D !important` },
                "& .MuiOutlinedInput-root:hover fieldset": { borderColor: `${FRESENIUS.white}80 !important` },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${FRESENIUS.white} !important` },
              }}
              inputProps={{
                autoComplete: "current-password",
              }}
            />
          </MDBox>
          <MDBox mt={4} mb={1}>
            <MDButton
              sx={{
                background: FRESENIUS.blue,
                color: `${FRESENIUS.white} !important`,
                fontSize: "1.1rem",
                fontWeight: "medium",
                py: 1.5,
                borderRadius: "12px",
                "&:hover": { background: FRESENIUS.blueLight, opacity: 0.9 },
              }}
              fullWidth
              onClick={handleCredentialsSubmit}
              endIcon={<Icon sx={{ color: `${FRESENIUS.white} !important` }}>arrow_forward</Icon>}
            >
              {t("signIn.signIn")}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );

  // Render 2FA setup view (first time)
  const render2FASetupView = () => (
    <Card
      sx={{
        backgroundColor: cardBg,
        backdropFilter: "blur(10px)",
        border: cardBorder,
        borderRadius: "20px",
        maxWidth: "500px",
      }}
    >
      <MDBox pt={3} pb={2} px={3}>
        {/* Heading Section */}
        <MDBox textAlign="center" mb={2}>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color="white"
            sx={{
              background: textGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("signIn.setup2FATitle")}
          </MDTypography>
        </MDBox>
        <MDBox textAlign="center" mb={2}>
          <MDTypography variant="body1" color="white">
            {t("signIn.setup2FADescription")}
          </MDTypography>
        </MDBox>

        {/* QR Code Display - white square with rounded borders */}
        <MDBox
          sx={{
            backgroundColor: "white",
            borderRadius: "24px",
            p: 3,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
        >
          <MDBox
            component="img"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
              qrCodeURI,
            )}`}
            alt="2FA QR Code"
            sx={{ width: "200px", height: "200px", display: "block" }}
          />
        </MDBox>

        {/* Manual Entry Code */}
        <MDBox
          sx={{
            backgroundColor: `${inputBg}`,
            borderRadius: "12px",
            p: 1.5,
            mb: 2,
          }}
        >
          <MDTypography
            variant="caption"
            color="white"
            sx={{ opacity: 0.7 }}
            mb={1}
            display="block"
          >
            {t("signIn.manualEntryCode")}
          </MDTypography>
          <MDTypography
            variant="h6"
            color="white"
            fontWeight="bold"
            sx={{
              fontFamily: "monospace",
              letterSpacing: "0.1em",
              wordBreak: "break-all",
            }}
          >
            {get2FASecret()}
          </MDTypography>
        </MDBox>

        {/* Verification Code Input */}
        <MDBox mb={1.5}>
          <MDTypography variant="body2" color="white" mb={1}>
            {t("signIn.enter6DigitCode")}
          </MDTypography>
          <MDInput
            type="text"
            label={t("signIn.codeLabel")}
            value={twoFactorCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setTwoFactorCode(value);
            }}
            onKeyPress={(e) => handleKeyPress(e, handle2FASetup)}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: `${inputBg} !important`,
                color: `${FRESENIUS.white} !important`,
              },
              "& .MuiInputBase-input": {
                color: `${FRESENIUS.white} !important`,
                backgroundColor: "transparent !important",
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: `${FRESENIUS.white} !important`,
                  caretColor: `${FRESENIUS.white} !important`,
                },
                "&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: `${FRESENIUS.white} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": { color: `${FRESENIUS.white}80`, opacity: 1 },
              "& .MuiInputLabel-root": { color: `${FRESENIUS.white}B3 !important` },
              "& .MuiInputLabel-root.Mui-focused": { color: `${FRESENIUS.white} !important` },
              "& .MuiOutlinedInput-root fieldset": { borderColor: `${FRESENIUS.white}4D !important` },
              "& .MuiOutlinedInput-root:hover fieldset": { borderColor: `${FRESENIUS.white}80 !important` },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${FRESENIUS.white} !important` },
            }}
            inputProps={{
              maxLength: 6,
              autoComplete: "off",
              style: {
                fontSize: "1.5rem",
                letterSpacing: "0.5em",
                textAlign: "center",
                fontFamily: "monospace",
                color: "white",
              },
            }}
          />
        </MDBox>

        <MDBox mt={3} mb={1}>
          <MDButton
            sx={{
              background: FRESENIUS.blue,
              color: `${FRESENIUS.white} !important`,
              fontSize: "1.1rem",
              fontWeight: "medium",
              py: 1.5,
              borderRadius: "12px",
              "&:hover": { background: FRESENIUS.blueLight, opacity: 0.9 },
            }}
            fullWidth
            onClick={handle2FASetup}
            endIcon={<Icon sx={{ color: `${FRESENIUS.white} !important` }}>arrow_forward</Icon>}
          >
            {t("signIn.confirmAndComplete")}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );

  // Render 2FA verification view (subsequent logins)
  const render2FAVerifyView = () => (
    <Card
      sx={{
        backgroundColor: cardBg,
        backdropFilter: "blur(10px)",
        border: cardBorder,
        borderRadius: "20px",
      }}
    >
      <MDBox pt={4} pb={3} px={3}>
        {/* Heading Section */}
        <MDBox textAlign="center" mb={3}>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color="white"
            mb={2}
            sx={{
              background: textGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("signIn.twoFactorTitle")}
          </MDTypography>
        </MDBox>
        <MDBox textAlign="center" mb={3}>
          <Icon
            onClick={(e) => {
              if (e.shiftKey && e.ctrlKey) {
                completeLogin();
              }
            }}
            sx={{
              fontSize: "3rem !important",
              color: FRESENIUS.blueLight,
              mb: 2,
              cursor: "pointer",
            }}
          >
            phonelink_lock
          </Icon>
          <MDTypography variant="body1" color="white" mb={2}>
            {t("signIn.twoFactorDescription")}
          </MDTypography>
        </MDBox>

        <MDBox mb={2}>
          <MDInput
            type="text"
            label={t("signIn.codeLabel")}
            value={twoFactorCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setTwoFactorCode(value);
            }}
            onKeyPress={(e) => handleKeyPress(e, handle2FAVerify)}
            fullWidth
            autoFocus
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: `${inputBg} !important`,
                color: `${FRESENIUS.white} !important`,
              },
              "& .MuiInputBase-input": {
                color: `${FRESENIUS.white} !important`,
                backgroundColor: "transparent !important",
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: `${FRESENIUS.white} !important`,
                  caretColor: `${FRESENIUS.white} !important`,
                },
                "&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: `${FRESENIUS.white} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": { color: `${FRESENIUS.white}80`, opacity: 1 },
              "& .MuiInputLabel-root": { color: `${FRESENIUS.white}B3 !important` },
              "& .MuiInputLabel-root.Mui-focused": { color: `${FRESENIUS.white} !important` },
              "& .MuiOutlinedInput-root fieldset": { borderColor: `${FRESENIUS.white}4D !important` },
              "& .MuiOutlinedInput-root:hover fieldset": { borderColor: `${FRESENIUS.white}80 !important` },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${FRESENIUS.white} !important` },
            }}
            inputProps={{
              maxLength: 6,
              autoComplete: "off",
              style: {
                fontSize: "1.5rem",
                letterSpacing: "0.5em",
                textAlign: "center",
                fontFamily: "monospace",
                color: "white",
              },
            }}
          />
        </MDBox>

        <MDBox mt={4} mb={1}>
          <MDButton
            sx={{
              background: FRESENIUS.blue,
              color: `${FRESENIUS.white} !important`,
              fontSize: "1.1rem",
              fontWeight: "medium",
              py: 1.5,
              borderRadius: "12px",
              "&:hover": { background: FRESENIUS.blueLight, opacity: 0.9 },
            }}
            fullWidth
            onClick={handle2FAVerify}
            endIcon={<Icon sx={{ color: `${FRESENIUS.white} !important` }}>arrow_forward</Icon>}
          >
            {t("signIn.confirm")}
          </MDButton>
        </MDBox>

        <MDBox mt={2} textAlign="center">
          <MDButton
            variant="text"
            sx={{
              color: "white",
              opacity: 0.7,
              "&:hover": { opacity: 1, background: "transparent" },
            }}
            onClick={() => {
              setLoginStep("credentials");
              setCredentials({ email: "", password: "" });
              setTwoFactorCode("");
            }}
          >
            {t("signIn.backToSignIn")}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );

  return (
    <MDBox
      sx={{
        position: "absolute",
        width: "100%",
        minHeight: "100vh",
        backgroundColor: FRESENIUS.blueDark,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {renderSuccess}
      {renderError}
      <MDBox
        px={1}
        width="100%"
        height="100vh"
        mx="auto"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        overflow="hidden"
      >
        {/* Logo Section - Fresenius + SBI (Ctrl+Shift+click on SBI logo to skip 2FA) */}
        <MDBox textAlign="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={3}
            flexWrap="wrap"
          >
            <img
              src={`${process.env.PUBLIC_URL}/fresenius_logo_white.png`}
              alt="Fresenius Kabi"
              style={{ height: "56px", width: "auto" }}
            />
            <img
              src={`${process.env.PUBLIC_URL}/logo_with_text_light.svg`}
              alt="Safe Brain Initiative"
              style={{ height: "40px", width: "auto", opacity: 0.9 }}
              onClick={(e) => {
                if (e.shiftKey && e.ctrlKey) completeLogin();
              }}
              role="img"
            />
          </MDBox>
        </MDBox>

        <MDBox width="100%" maxWidth="550px" px={3}>
          {loginStep === "credentials" && renderCredentialsView()}
          {loginStep === "2fa-setup" && render2FASetupView()}
          {loginStep === "2fa-verify" && render2FAVerifyView()}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default SignIn;
